import React, { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { OrderType } from 'models/order.model';
import RiderDetailsSideModal from 'pages/logistics/partners/partner-modals/rider-details-modal';
import { ReactComponent as UserIcon } from 'assets/icons/user.svg';
import { ReactComponent as RiderIcon } from 'assets/icons/motorbike.svg';
import { ReactComponent as CurrencyIcon } from 'assets/icons/coin.svg';
import { ReactComponent as LocationIcon } from 'assets/icons/location.svg';
import { ClockIcon, PickupIcon } from 'cokitchen-icons';
import useRoleContext from 'contexts/role.context';
import { formatNumber, getNameFromOrder, getOrderStatus, getUserName } from 'utils/functions';
import { calculateDeliveryTime, calTotalKitchenTime } from 'pages/order-details/util';
import OrderTag from 'components/base/order-tag';
import { ENUM_STATUS } from 'utils/enum';

type LiveTripCardDetailsProps = {
  order: OrderType;
};

const BaseLiveTripCardBody: FC<LiveTripCardDetailsProps> = ({ order }) => {
  const [showRiderProfile, setShowRiderProfile] = useState(false);

  const surge = order.surge?.logistics_surge && order.surge?.logistics_surge !== '1.0';
  const late = order.kitchen_time_elapsed;
  const name = getNameFromOrder(order);

  const rider_name = getUserName(order.rider, 'nil');
  const delivery_fee = order?.calculated_order?.delivery_fee;
  const address_details = order?.calculated_order?.address_details.address_line;

  const { role } = useRoleContext();
  const orderStatus = useMemo(() => getOrderStatus(order), [order]);

  const [timeDifference, setTimeDifference] = useState({
    text: '',
    value: 0
  });

  const updateTimeDifference = ({ time, text }: { time: string; text: string }) => {
    const td = new Date(time).getTime() - new Date().getTime();
    const value = Math.floor((td / 1000 / 60) % 60);
    setTimeDifference({ text, value });
  };

  useEffect(() => {
    if (!role || order.pickup) return;

    const diffTime = calculateDeliveryTime({ order, orderStatus, role });
    updateTimeDifference(diffTime);

    const interval = setInterval(() => calculateDeliveryTime({ order, orderStatus, role }), 60000);
    return () => clearInterval(interval);
  }, [order, orderStatus, role]);

  const timeDifferenceStyle =
    timeDifference.value > 15
      ? 'border border-[#ECEFEB] bg-[#EEF9EB] text-[#46AE31]'
      : timeDifference.value <= 15 && timeDifference.value > 5
      ? 'border border-[#EFEEEB] bg-[#F9F6EB] text-[#BD8F05]'
      : 'border border-[#EFEBEC] bg-[#FAEFF1] text-[#E60A2B]';

  return (
    <Fragment>
      <div className='px-4 py-3 space-y-2 grow'>
        <div className='flex justify-between items-center text-sm gap-10'>
          <div className='flex items-center gap-2 flex-wrap'>
            <h4 className='font-medium  text-base text-gray-6'>
              #{order?.order_code?.toUpperCase() ?? ''}
            </h4>
            {surge && <OrderTag type='surge' />}
            {late && <OrderTag type='late' />}
          </div>

          <p className='flex min-w-fit items-center gap-2'>
            <UserIcon />
            {name}
          </p>
        </div>
        <div className='flex justify-between items-center text-sm'>
          <p className='flex items-center gap-2'>
            {order?.pickup ? (
              <>
                <PickupIcon />
                pickup
              </>
            ) : (
              <>
                <RiderIcon className='w-4 min-w-[16px]' />
                <span
                  onClick={() => {
                    setShowRiderProfile(true);
                  }}
                  className={rider_name ? '' : 'text-red-4'}>
                  {rider_name ?? 'No rider yet'}
                </span>
              </>
            )}
          </p>
          <p className='flex items-center gap-2'>
            <CurrencyIcon className='w-4 min-w-[16px]' />
            {formatNumber(delivery_fee)}
          </p>
        </div>
        <p className='flex items-start gap-2'>
          <LocationIcon className='mt-[4px] min-w-[16px]' /> <span>{address_details}</span>
        </p>
        {[ENUM_STATUS.ONGOING, ENUM_STATUS.READY].includes(orderStatus) && (
          <div className='flex'>
            <div className='text-sm text-gray-8 flex items-center gap-2'>
              <ClockIcon className='text-gray-4 min-w-[16px]' />
              <span>Total kitchen time:</span>
            </div>
            <div className='text-[10px] rounded-[40px] ml-1 py-[3px] px-[10px] border border-[#EFEBEC] bg-[#EDEDED] text-[#858585]'>
              {' '}
              {calTotalKitchenTime({ order, orderStatus })} mins
            </div>
          </div>
        )}

        {timeDifference.text ? (
          <div className='flex gap-1 my-1 items-center'>
            <div className='flex'>
              <div className='text-sm text-gray-8 flex items-center gap-2'>
                <ClockIcon className='text-gray-4 min-w-[16px]' />
                <span>{timeDifference.text}: </span>
              </div>
            </div>
            <div className={`text-[10px] rounded-[40px] py-[3px] px-[10px] ${timeDifferenceStyle}`}>
              {timeDifference.value ? `${timeDifference.value} mins` : '--'}
            </div>
          </div>
        ) : null}
      </div>
      {order?.rider ? (
        <RiderDetailsSideModal
          rider={order?.rider}
          show={showRiderProfile}
          onClose={() => setShowRiderProfile(false)}
        />
      ) : null}
    </Fragment>
  );
};

export default BaseLiveTripCardBody;
